import { create } from 'zustand';
import type { FacilitySettings } from '../types/facility';
import type { SecurityRole } from '../types/user';
import { displayEhrTitle } from '../utils/ehrDisplayName';

// userId starts at 0 (unresolved). Auth flow (AuthTokenSetter or DebugTokenSetter) sets the real value.
// Components should wait for userId > 0 before making user-scoped API calls.

export const FACILITY_STORAGE_KEY = 'dietech_facility';

function loadSavedFacility(): { id: number; name: string } | null {
  try {
    const saved = sessionStorage.getItem(FACILITY_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed?.id > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

const savedFacility = loadSavedFacility();

/**
 * The Temp Menu (WinForms g_FKey_Menu_Hdr_Override) lives in sessionStorage: it survives a reload in the
 * same tab and ends with the tab, "for your session only". It is saved with the facility that is open, so
 * a reload restores it for that facility only. A facility switch carries it over (see selectFacility).
 */
export const TEMP_MENU_STORAGE_KEY = 'dietech_temp_menu';

interface SavedTempMenu { facilityId: number; id: number; name: string }

function loadSavedTempMenu(facilityId: number): SavedTempMenu | null {
  try {
    const saved = sessionStorage.getItem(TEMP_MENU_STORAGE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as Partial<SavedTempMenu> | null;
    if (parsed && Number(parsed.id) > 0 && facilityId > 0 && Number(parsed.facilityId) === facilityId) {
      return { facilityId, id: Number(parsed.id), name: String(parsed.name ?? '') };
    }
  } catch {
    // ignore
  }
  return null;
}

function saveTempMenu(value: SavedTempMenu | null) {
  try {
    if (value) sessionStorage.setItem(TEMP_MENU_STORAGE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(TEMP_MENU_STORAGE_KEY);
  } catch {
    // ignore
  }
}

const savedTempMenu = loadSavedTempMenu(savedFacility?.id ?? 0);

/**
 * The role a signed-in user with no security role (FKey_PM_Security_Roles = 0) gets: every level
 * missing, so every check reads 0 (no access). Distinct from `null`, which means "not loaded yet".
 */
export const NO_ROLE = Object.freeze({}) as SecurityRole;

interface GlobalState {
  facilitySettings: FacilitySettings | null;
  facilitySettingsLoaded: boolean;

  // Current user (hardcoded from env in dev, set from auth in production)
  userId: number;
  userName: string;
  userFirstName: string;
  userLastName: string;
  userEmail: string;

  // Selected facility within the current database connection
  facilityId: number;
  facilityName: string;

  // True when /Me resolved a user but they have no usable facility
  // (no DF_Dietech_Facility AND/OR no row in tblPMSec_Fac_Dietech for the saved DF)
  noFacilityAccess: boolean;
  setNoFacilityAccess: (v: boolean) => void;

  /**
   * Set when signing in could not finish: the user's facility list or security role could not be
   * read. The app shows this with a Try again button instead of carrying on without them, since
   * without the role every permission check reads "no access" and without the facility list there
   * is no facility the user is known to be allowed in.
   */
  sessionLoadError: string | null;
  setSessionLoadError: (message: string | null) => void;
  /**
   * The API refused this sign-in on purpose (an enterprise sign-in with no matching security role):
   * sessionLoadError then holds its message, and trying again won't help.
   */
  sessionLoadRefused: boolean;
  setSessionLoadRefused: (message: string) => void;
  /** Bumped by Try again; AuthTokenSetter runs its lookups again when it changes. */
  sessionRetryNonce: number;
  retrySessionLoad: () => void;
  /** Drops the selected facility, in memory and in sessionStorage. */
  clearFacility: () => void;

  setFacilitySettings: (settings: FacilitySettings) => void;
  clearFacilitySettings: () => void;
  // Security role
  userRole: SecurityRole | null;
  userRoleId: number;

  // Facility switching state (shows overlay during transition)
  isSwitchingFacility: boolean;
  setIsSwitchingFacility: (switching: boolean) => void;

  setUserId: (id: number) => void;
  setUserInfo: (id: number, userName: string, firstName: string, lastName: string, email?: string) => void;
  setUserRole: (role: SecurityRole | null, roleId: number) => void;
  selectFacility: (id: number, name: string) => void;

  // Temp menu override (session-only, like WinForms g_FKey_Menu_Hdr_Override). Kept in sessionStorage
  // with the open facility. A facility switch keeps it, as WinForms does (the global is not touched
  // by its facility picker); the top bar then turns it off if the new facility does not use that menu.
  tempMenuOverrideId: number | null;
  tempMenuOverrideName: string;
  /** Name of the facility a kept Temp Menu was carried over from, until the top bar has checked it
   *  against the new facility's menus; null when the Temp Menu was picked in the open facility. */
  tempMenuCarriedFrom: string | null;
  /** The top bar found the carried-over Temp Menu in the new facility's menus. */
  confirmTempMenuCarriedOver: () => void;
  setTempMenuOverride: (id: number | null, name: string) => void;
  clearTempMenuOverride: () => void;

  // Full-screen mode - hides main sidebar (e.g., Production Menu)
  sidebarHidden: boolean;
  setSidebarHidden: (hidden: boolean) => void;

  // Force sidebar into collapsed (icon-only) mode (e.g., Resident Detail)
  sidebarForceCollapsed: boolean;
  setSidebarForceCollapsed: (collapsed: boolean) => void;

  // A phone screen with its own bottom bar (a record's PhoneActionBar) puts
  // the tab bar away, so two bars do not stack. See useHidePhoneTabBar.
  phoneTabBarHidden: boolean;
  setPhoneTabBarHidden: (hidden: boolean) => void;

  // Label helpers with defaults
  residentLabel: string;
  residentPluralLabel: string;
  prescriberLabel: string;
  specialDeviceLabel: string;
  facilityLabel: string;
  /** Plural facility label word ("Facilities" / "Centers" / "Homes") —
   *  reads from `tblFacility.Facility_Plural` (WinForms
   *  `g_facilitySettings.Facility_Plural(fkey)`). Defaults to
   *  "Facilities" when unset. */
  facilityPluralLabel: string;
  /** "Floor" or "Unit" — swaps based on the facility's
   *  `useUnitMapping` setting. Used by report labels that read
   *  "Group By Floor" vs "Group By Unit" depending on how the
   *  facility organizes its patient rooms. */
  floorOrUnitLabel: string;
  /** Facility-configured label for the EHR/EMR system —
   *  `tblFacility.EHR_Title` column (e.g. "EMR", "EHR", "Meditech").
   *  Used by report categories/labels that reference the
   *  EHR/EMR integration. Defaults to "EMR" when unset. */
  ehrTitleLabel: string;
  roomLabel: string;
  setNameLabel: string;
  admitLabel: string;
  breakfastName: string;
  lunchName: string;
  dinnerName: string;
  nourishEamName: string;
  nourishAmName: string;
  nourishPmName: string;
  nourishHsName: string;
  nourishPm2Name: string;
  nourishHs2Name: string;
  /** Facility-scoped label for the "Credits" concept — WinForms
   *  reads this from `FacilitySettings.Credits_MenuTitle`
   *  (`Credits_MenuTitle` column on tblFacility). Facilities can
   *  rename to "Bucks", "Points", "Meal Credits", etc. Defaults
   *  to "Credits" when unset. */
  creditsLabel: string;
}

export const useGlobalStore = create<GlobalState>((set, get) => ({
  facilitySettings: null,
  facilitySettingsLoaded: false,
  userId: 0,
  userName: '',
  userFirstName: '',
  userLastName: '',
  userEmail: '',
  userRole: null,
  userRoleId: 0,
  sidebarHidden: false,
  setSidebarHidden: (hidden) => set({ sidebarHidden: hidden }),
  sidebarForceCollapsed: false,
  setSidebarForceCollapsed: (collapsed) => set({ sidebarForceCollapsed: collapsed }),
  phoneTabBarHidden: false,
  setPhoneTabBarHidden: (hidden) => set({ phoneTabBarHidden: hidden }),
  isSwitchingFacility: false,
  facilityId: savedFacility?.id ?? 0,
  facilityName: savedFacility?.name ?? '',

  setFacilitySettings: (settings) => {
    // Case-insensitive key lookup so we tolerate whichever JSON
    // casing the API happens to emit for DB columns (the DB column
    // `EHR_Title` serializes as `eHR_Title`, not `ehR_Title`).
    // Mirrors the
    // WinForms `FacilitySettings.PopulateStringWithSettingValues`
    // pattern which is case-blind on lookup.
    const s = settings as Record<string, unknown>;
    const ci: Record<string, unknown> = {};
    for (const k of Object.keys(s)) ci[k.toLowerCase()] = s[k];
    const str = (key: string): string => {
      const v = ci[key.toLowerCase()];
      return typeof v === 'string' ? v : '';
    };
    return set({
      facilitySettings: settings,
      facilitySettingsLoaded: true,
      residentLabel: str('resident') || 'Resident',
      residentPluralLabel: str('resident_Plural') || 'Residents',
      prescriberLabel: str('prescriber') || 'Prescriber',
      specialDeviceLabel: str('specialDevice') || 'Special Device',
      facilityLabel: str('facility') || 'Facility',
      facilityPluralLabel: str('facility_Plural') || 'Facilities',
      floorOrUnitLabel: (ci['useunitmapping'] as boolean) ? 'Unit' : 'Floor',
      ehrTitleLabel: displayEhrTitle(str('eHR_Title')) || 'EHR Service',
      roomLabel: str('room') || 'Room',
      setNameLabel: str('setName') || 'Set',
      admitLabel: str('admit') || 'Admit',
      breakfastName: str('break_Name') || 'Breakfast',
      lunchName: str('lunch_Name') || 'Lunch',
      dinnerName: str('dinner_Name') || 'Dinner',
      nourishEamName: str('nourish_Name_EAM') || 'EAM',
      nourishAmName: str('nourish_Name_AM') || 'AM',
      nourishPmName: str('nourish_Name_PM') || 'PM',
      nourishHsName: str('nourish_Name_HS') || 'HS',
      nourishPm2Name: str('nourish_Name_PM2') || 'PM2',
      nourishHs2Name: str('nourish_Name_HS2') || 'HS2',
      creditsLabel: str('credits_MenuTitle') || 'Credits',
    });
  },

  clearFacilitySettings: () =>
    set({
      facilitySettings: null,
      facilitySettingsLoaded: false,
      residentLabel: 'Resident',
      residentPluralLabel: 'Residents',
      prescriberLabel: 'Prescriber',
      specialDeviceLabel: 'Special Device',
      facilityLabel: 'Facility',
      facilityPluralLabel: 'Facilities',
      floorOrUnitLabel: 'Floor',
      ehrTitleLabel: 'EHR Service',
      roomLabel: 'Room',
      setNameLabel: 'Set',
      admitLabel: 'Admit',
      breakfastName: 'Breakfast',
      lunchName: 'Lunch',
      dinnerName: 'Dinner',
      nourishEamName: 'EAM',
      nourishAmName: 'AM',
      nourishPmName: 'PM',
      nourishHsName: 'HS',
      nourishPm2Name: 'PM2',
      nourishHs2Name: 'HS2',
      creditsLabel: 'Credits',
    }),

  tempMenuOverrideId: savedTempMenu?.id ?? null,
  tempMenuOverrideName: savedTempMenu?.name ?? '',
  setTempMenuOverride: (id, name) => {
    if (id == null || !(id > 0)) {
      get().clearTempMenuOverride();
      return;
    }
    saveTempMenu({ facilityId: get().facilityId, id, name });
    set({ tempMenuOverrideId: id, tempMenuOverrideName: name, tempMenuCarriedFrom: null });
  },
  clearTempMenuOverride: () => {
    saveTempMenu(null);
    set({ tempMenuOverrideId: null, tempMenuOverrideName: '', tempMenuCarriedFrom: null });
  },
  tempMenuCarriedFrom: null,
  confirmTempMenuCarriedOver: () => set({ tempMenuCarriedFrom: null }),

  setIsSwitchingFacility: (switching) => set({ isSwitchingFacility: switching }),

  setUserId: (id) => set({ userId: id }),
  setUserInfo: (id, userName, firstName, lastName, email) => set({ userId: id, userName, userFirstName: firstName, userLastName: lastName, ...(email !== undefined && { userEmail: email }) }),
  setUserRole: (role, roleId) => set({ userRole: role, userRoleId: roleId }),

  selectFacility: (id, name) => {
    sessionStorage.setItem(FACILITY_STORAGE_KEY, JSON.stringify({ id, name }));
    const prev = get();
    if (id !== prev.facilityId) {
      // WinForms keeps g_FKey_Menu_Hdr_Override when the user changes facility (beFacility does not
      // touch it). Menus can be shared between facilities in the same database, so the Temp Menu is
      // kept here too and the top bar checks it against the new facility's menus: kept when that
      // facility uses the menu, turned off with a notice when it does not. Dropping the facility
      // (id 0, a connection change) still clears it: another database has other menus.
      const tempId = prev.tempMenuOverrideId;
      if (id > 0 && prev.facilityId > 0 && tempId != null && tempId > 0) {
        saveTempMenu({ facilityId: id, id: tempId, name: prev.tempMenuOverrideName });
        set({ tempMenuCarriedFrom: prev.facilityName || 'the previous facility' });
      } else {
        prev.clearTempMenuOverride();
      }
    }
    set({ facilityId: id, facilityName: name, noFacilityAccess: false });
  },

  noFacilityAccess: false,
  setNoFacilityAccess: (v) => set({ noFacilityAccess: v }),

  sessionLoadError: null,
  setSessionLoadError: (message) => set({ sessionLoadError: message, sessionLoadRefused: false }),
  sessionLoadRefused: false,
  setSessionLoadRefused: (message) => set({ sessionLoadError: message, sessionLoadRefused: true }),
  sessionRetryNonce: 0,
  retrySessionLoad: () =>
    set((s) => ({ sessionLoadError: null, sessionLoadRefused: false, sessionRetryNonce: s.sessionRetryNonce + 1 })),
  clearFacility: () => {
    try {
      sessionStorage.removeItem(FACILITY_STORAGE_KEY);
    } catch {
      // ignore
    }
    get().clearTempMenuOverride();
    set({ facilityId: 0, facilityName: '' });
  },

  residentLabel: 'Resident',
  residentPluralLabel: 'Residents',
  prescriberLabel: 'Prescriber',
  specialDeviceLabel: 'Special Device',
  facilityLabel: 'Facility',
  facilityPluralLabel: 'Facilities',
  floorOrUnitLabel: 'Floor',
  ehrTitleLabel: 'EHR Service',
  roomLabel: 'Room',
  setNameLabel: 'Set',
  admitLabel: 'Admit',
  breakfastName: 'Breakfast',
  lunchName: 'Lunch',
  dinnerName: 'Dinner',
  nourishEamName: 'EAM',
  nourishAmName: 'AM',
  nourishPmName: 'PM',
  nourishHsName: 'HS',
  nourishPm2Name: 'PM2',
  nourishHs2Name: 'HS2',
  creditsLabel: 'Credits',
}));
